import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { closeIcon, saveIcon } from "assets/images";
import CalendarIcon from "components/CalendarIcon";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  DATE_ONLY_FORMAT,
  DATE_PICKER_FORMAT,
  INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
} from "utils/constants/constant";
import { ratingDisplayMap, VARIANCE_MAX_LENGTH } from "utils/constants/slp";
import {
  getAchievementRatingError,
  getAssessedDateError,
  getVarianceError,
} from "utils/helper/validation-message-helper";
import {
  IAddEditGoalsAssessment,
  IPersonalSmartGoals,
} from "utils/interfaces/slp-form";

type IAssessGoalModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IAddEditGoalsAssessment) => void;
  goalData: IPersonalSmartGoals | null;
};

const AssessGoalModal: React.FC<IAssessGoalModalProps> = ({
  open,
  onClose,
  onSubmit,
  goalData,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    reset,
  } = useForm<IAddEditGoalsAssessment>();

  useEffect(() => {
    if (goalData) {
      reset({
        slpGoalsId: goalData.id ?? 0,
        goalAssessedDate: goalData.goalAssessedDate
          ? dayjs(goalData.goalAssessedDate)
          : null,
        achievementRatingGAS: goalData.achievementRatings ?? undefined,
        variance: goalData.variance ?? "",
      });
    }
  }, [goalData, reset]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="common-modal modal-600">
        <Box className="modal-header">
          <Typography variant="h4">Assess Goal Attainment</Typography>
          <IconButton onClick={onClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>
        <form
          onSubmit={handleSubmit((data) =>
            onSubmit({
              ...data,
              goalAssessedDate: dayjs(data.goalAssessedDate).format(
                DATE_ONLY_FORMAT
              ),
            })
          )}
          noValidate
        >
          <Box className="modal-body">
            <Grid container columnSpacing="10px">
              <Grid item xs={12} mb={3}>
                <Typography variant="h5" fontWeight="600">
                  Baseline Level
                </Typography>
                <Typography variant="body1" mt={1}>
                  {goalData?.baselineLevel}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DesktopDatePicker"]}>
                    <Controller
                      name="goalAssessedDate"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <DesktopDatePicker
                          {...field}
                          className="date-picker"
                          label={
                            <>
                              Date Goal Assessed{" "}
                              <span className="color-red">*</span>
                            </>
                          }
                          format={DATE_PICKER_FORMAT}
                          value={watch("goalAssessedDate")}
                          minDate={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.goalAssessedDate,
                              helperText: getAssessedDateError(
                                errors.goalAssessedDate
                              ),
                            },
                          }}
                          slots={{
                            openPickerIcon: CalendarIcon,
                          }}
                        />
                      )}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ mt: { xs: 3, sm: 0 } }}>
                <FormControl
                  fullWidth
                  className="select"
                  error={!!errors.achievementRatingGAS}
                >
                  <InputLabel>
                    Achievement Rating (GAS){" "}
                    <span className="color-red">*</span>
                  </InputLabel>
                  <Controller
                    name="achievementRatingGAS"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select label="Achievement Rating (GAS)*" {...field}>
                        {Object.entries(ratingDisplayMap)
                          .sort(([a], [b]) => Number(b) - Number(a))
                          .map(([value, label]) => (
                            <MenuItem key={value} value={Number(value)}>
                              {label} {value}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {getAchievementRatingError(errors.achievementRatingGAS)}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Variance"
                  multiline
                  className="suggestion-textarea"
                  fullWidth
                  error={!!errors.variance}
                  helperText={getVarianceError(errors.variance)}
                  value={watch("variance")}
                  variant="outlined"
                  {...register("variance", {
                    maxLength: VARIANCE_MAX_LENGTH,
                    pattern:
                      INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
                  })}
                />
              </Grid>
            </Grid>
          </Box>
          <Box className="modal-footer">
            <Button type="submit" variant="contained" className="btn-save">
              <img src={saveIcon} alt="save" />
              Save
            </Button>
            <Button variant="outlined" className="btn-cancel" onClick={onClose}>
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default AssessGoalModal;
